"use client";

import { getPostalCodeFormatErrorMessage } from "@homeinstead/locale";
import { cn } from "@homeinstead/ui";
import { Input } from "@homeinstead/ui/components/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@homeinstead/ui/components/Select";
import { useCallback, useEffect, useId } from "react";
import { useAnswersLoaded } from "../../hooks/use-answers";
import { useCopy } from "../../hooks/use-copy";
import { useOfficeSearch } from "../../hooks/use-office-search";
import { useLeadFlowAnswerStore } from "../LeadFlowAnswerStoreProvider";
import { useLeadFlowStep } from "../LeadFlowStepContext";
import { FieldError } from "./FieldError";
import { QuestionLegend } from "./QuestionLegend";

/** The submit action sends a lead under this number without a franchise. */
const NATIONAL_ORGANIZATION_NUMBER = 0;
type PostalCodeQuestionProps = {
  item: {
    helperText?: string | null;
    label?: string | null;
    placeholder?: string | null;
  };
};

type OfficeSelection = {
  location: string;
  organizationNumber: number | undefined;
  postalCode: string | undefined;
};

function useOfficeSelectionWriter() {
  const store = useLeadFlowAnswerStore();

  return useCallback(
    (selection: OfficeSelection) => {
      store.setAnswer("location", selection.location || undefined);
      store.setAnswer("postalCode", selection.postalCode);
      store.setAnswer("organizationNumber", selection.organizationNumber);
    },
    [store],
  );
}

/**
 * Keeps the as-is postal semantics: `location` is what the visitor typed,
 * while `postalCode` and `organizationNumber` belong to the office they pick.
 * The nearest office is picked by default so the visitor can continue
 * without an extra interaction.
 */
function PostalCodeQuestionFields({ item }: PostalCodeQuestionProps) {
  const store = useLeadFlowAnswerStore();
  const hasLoadedAnswers = useAnswersLoaded();
  const { errors, isInert = false, setIsAnswerPending } = useLeadFlowStep();
  const writeOfficeSelection = useOfficeSelectionWriter();
  const copy = useCopy();
  const inputId = useId();
  const errorId = useId();

  const {
    hasLookupError,
    isFetching,
    isFormatValid,
    isRestorePending,
    localeCode,
    officeOptions,
    selectedOption,
    selectedOptionId,
    selectOffice,
    typedLocation,
    typeLocation,
  } = useOfficeSearch({
    getInitialState: () => {
      const answers = store.getSnapshot();
      return {
        preferredOfficeId: answers.organizationNumber ?? null,
        preferredPostalCode: answers.postalCode ?? null,
        typedLocation: answers.location ?? answers.postalCode ?? "",
      };
    },
    isLookupEnabled: !isInert,
  });
  const showFormatError = typedLocation.trim() !== "" && !isFormatValid;
  // When the lookup itself fails the visitor continues without an office:
  // the typed code is submitted as the postal code under the national
  // organization number, which the submit action routes without a franchise
  // and which still counts as resolved when a later step is restored.
  const isContinuingWithoutOffice = isFormatValid && hasLookupError;
  const selectedPostalCode = isContinuingWithoutOffice
    ? typedLocation.trim()
    : selectedOption?.address.postalCode;
  const selectedOrganizationNumber = isContinuingWithoutOffice
    ? NATIONAL_ORGANIZATION_NUMBER
    : selectedOption?.officeID;

  // A valid code with no office yet is still resolving: the request has not
  // settled, or its result has not been applied to the selection. Writing
  // then would clear a prefilled or restored postal code. A restored office
  // id waits on its own, until the lookup can confirm that location.
  const isSelectionUnresolved =
    isFormatValid &&
    !hasLookupError &&
    selectedOption == null &&
    (isFetching || officeOptions.length > 0);

  useEffect(() => {
    if (
      isInert ||
      !hasLoadedAnswers ||
      isRestorePending ||
      isSelectionUnresolved
    ) {
      return;
    }

    writeOfficeSelection({
      location: typedLocation,
      organizationNumber: selectedOrganizationNumber,
      postalCode: selectedPostalCode,
    });
  }, [
    hasLoadedAnswers,
    isInert,
    isRestorePending,
    isSelectionUnresolved,
    selectedOrganizationNumber,
    selectedPostalCode,
    typedLocation,
    writeOfficeSelection,
  ]);

  const postalCodeErrors = showFormatError
    ? [getPostalCodeFormatErrorMessage(localeCode)]
    : typedLocation.trim() === ""
      ? errors.postalCode
      : undefined;
  const showOfficeSelect = isFormatValid && !hasLookupError;
  // A background refetch is also `isFetching`, and the offices already on
  // screen stay usable until it replaces them.
  const isWaitingForFirstResult = isFetching && officeOptions.length === 0;
  const isLoadingOffices = showOfficeSelect && isWaitingForFirstResult;

  // A typed code needs an office before Continue, so the step waits for the
  // lookup to auto-select one or offer the choice.
  useEffect(() => {
    if (isInert || !setIsAnswerPending) {
      return;
    }

    setIsAnswerPending(isLoadingOffices);
    return () => setIsAnswerPending(false);
  }, [isLoadingOffices, isInert, setIsAnswerPending]);
  // A lone office is already selected by the search machine, so the choice
  // is only shown when there is a real alternative.
  const isOfficeSelectVisible =
    showOfficeSelect && !isWaitingForFirstResult && officeOptions.length > 1;
  const locationErrors =
    isOfficeSelectVisible && !selectedOption ? errors.location : undefined;
  const hasNoMatchingOffices =
    showOfficeSelect &&
    !isWaitingForFirstResult &&
    !hasLookupError &&
    officeOptions.length === 0;

  return (
    <fieldset>
      <QuestionLegend helperText={item.helperText} label={item.label} />
      {/* Homepage location step: the postal field shares the step width.
          Office UI only mounts when there is something to show. Gap matches
          ContactQuestion's field stack. */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label className="sr-only" htmlFor={inputId}>
            {item.label || copy.postalCodeLabel}
          </label>
          <Input
            aria-describedby={postalCodeErrors?.length ? errorId : undefined}
            aria-invalid={postalCodeErrors?.length ? true : undefined}
            autoComplete="postal-code"
            className={cn({ "border-red-500": postalCodeErrors?.length })}
            id={inputId}
            onChange={(event) => typeLocation(event.currentTarget.value)}
            placeholder={item.placeholder ?? undefined}
            // This instance remounts once stored answers load, so anything typed
            // before then would be lost.
            readOnly={!hasLoadedAnswers}
            type="text"
            value={typedLocation}
          />
          <FieldError id={errorId} messages={postalCodeErrors} />
        </div>
        {isLoadingOffices ? (
          <p className="text-sm">{copy.loadingOfficesLabel}</p>
        ) : null}
        {isOfficeSelectVisible ? (
          <Select
            onValueChange={selectOffice}
            value={selectedOptionId ?? undefined}
          >
            <SelectTrigger label="Office">
              <SelectValue placeholder={copy.selectOfficePlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {officeOptions.map((option) => (
                <SelectItem key={option.optionId} value={option.optionId}>
                  {option.locationName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        {hasNoMatchingOffices ? (
          <p className="text-error text-sm">{copy.noOfficesFound}</p>
        ) : null}
        <FieldError id={`${errorId}-location`} messages={locationErrors} />
      </div>
    </fieldset>
  );
}

function PostalCodeQuestion({ item }: PostalCodeQuestionProps) {
  const hasLoadedAnswers = useAnswersLoaded();

  // The search machine reads stored answers only when it is created, so
  // this remounts it once those answers have loaded.
  return (
    <PostalCodeQuestionFields
      item={item}
      key={hasLoadedAnswers ? "loaded" : "loading"}
    />
  );
}

export { PostalCodeQuestion };
